import React, { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import EnvoiCourriel from './EnvoiCourriel';
import Layout from './Layout';
import {
  creerTache, envoyerAuDossierRelie, listerLiaisons, listerTaches,
  listerToutesLesPieces, majTache, traiterPiecesRecues,
} from './api';

const dateCourte = (v) => (v ? new Date(v).toLocaleDateString('fr-BE') : '');

// Les traces laissées par la GED (« Création de fichier … ») ne sont pas des
// choses à faire : l'assuré n'a pas à les voir.
const estUneTrace = (t) => ['FILE', 'XFILE'].includes(t.hierarchy);
const estRecu = (t) => t.hierarchy === 'XRECU';
const estFigee = (t) => (t.hierarchy || '').toLowerCase().startsWith('x');

const STATUTS_PIECE = {
  'à reprendre': 'warning',
  reprise: 'success',
  'écartée': 'default',
};

/** Le message tapé est du texte : on l'échappe avant d'en faire du HTML. */
const versHtml = (texte) => texte
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .split(/\n{2,}/)
  .map((p) => `<p>${p.replace(/\n/g, '<br>')}</p>`)
  .join('');

function Bloc({ titre, explication, children }) {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3.5 } }}>
      <Typography variant="h3" sx={{ mb: explication ? 0.5 : 2 }}>{titre}</Typography>
      {explication && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {explication}
        </Typography>
      )}
      {children}
    </Paper>
  );
}

/** Un envoi reçu d'un dossier relié, et sa zone « à reprendre ». */
function EnvoiRecu({ tache, onTraiter, occupe }) {
  const recu = tache.envoi_recu;
  const [choisies, setChoisies] = useState([]);
  if (!recu) return null;
  const enAttente = recu.pieces.filter((p) => p.statut === 'à reprendre');
  const basculer = (id) => setChoisies((l) => (
    l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));
  const traiter = async (action) => {
    await onTraiter(choisies, action);
    setChoisies([]);
  };

  return (
    <Box sx={{ py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {tache.title_bis || tache.title}
        </Typography>
        {enAttente.length > 0 && (
          <Chip size="small" color="warning"
                label={`${enAttente.length} à reprendre`} />
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary">
        De {recu.organisation}{recu.reference ? ` (dossier ${recu.reference})` : ''}
        {' · '}{dateCourte(recu.envoye_le)}
      </Typography>
      {/* Le message a été nettoyé à la réception : seules des balises de
          mise en forme y restent. */}
      {tache.comment && (
        <Box sx={{ mt: 1, '& p': { m: 0, mb: 1 } }}
             dangerouslySetInnerHTML={{ __html: tache.comment }} />
      )}

      {recu.pieces.length > 0 && (
        <Stack sx={{ mt: 1 }}>
          {recu.pieces.map((p) => (
            <Stack key={p.id} direction="row" alignItems="center" spacing={1}>
              <Checkbox size="small" checked={choisies.includes(p.id)}
                        disabled={p.statut !== 'à reprendre'}
                        onChange={() => basculer(p.id)} />
              <Link href={p.url} target="_blank" rel="noopener" underline="hover"
                    sx={{ flexGrow: 1 }}>
                {p.titre || p.nom}
              </Link>
              <Chip size="small" variant="outlined" label={p.statut}
                    color={STATUTS_PIECE[p.statut] || 'default'} />
            </Stack>
          ))}
        </Stack>
      )}

      {enAttente.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
          <Button size="small" variant="contained"
                  disabled={!choisies.length || occupe}
                  onClick={() => traiter('reprendre')}>
            Reprendre dans mes documents
          </Button>
          <Button size="small" color="inherit"
                  disabled={!choisies.length || occupe}
                  onClick={() => traiter('ecarter')}>
            Écarter
          </Button>
        </Stack>
      )}
    </Box>
  );
}

/** Où en est un lien de téléchargement : ouvert, pas encore, expiré. */
function etatDuLien(lien) {
  if (lien.telechargements > 0) {
    return { texte: `ouvert le ${dateCourte(lien.dernier_telechargement)}`, couleur: 'success' };
  }
  if (!lien.valide) return { texte: 'expiré sans avoir été ouvert', couleur: 'default' };
  return { texte: `pas encore ouvert · valable jusqu'au ${dateCourte(lien.expire_le)}`,
           couleur: 'warning' };
}

/** Un courriel parti : à qui, quand, ce qu'il portait, et si on l'a lu. */
function CourrielEnvoye({ tache }) {
  return (
    <Box sx={{ py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {tache.title_bis || tache.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
        {dateCourte(tache.modified_at)} · à {tache.receiver}
        {tache.copy_receiver ? ` · copie ${tache.copy_receiver}` : ''}
      </Typography>
      <Stack sx={{ mt: 0.5 }} spacing={0.25}>
        {(tache.pieces_jointes || []).map((p) => (
          <Typography key={p.id} variant="body2">
            Joint :{' '}
            {p.url ? (
              <Link href={p.url} target="_blank" rel="noopener" underline="hover">
                {p.titre || 'document'}
              </Link>
            ) : (p.titre || 'document')}
          </Typography>
        ))}
        {(tache.transferts || []).map((l) => {
          const etat = etatDuLien(l);
          return (
            <Stack key={l.jeton} direction="row" spacing={1} alignItems="center"
                   flexWrap="wrap" useFlexGap>
              <Typography variant="body2">
                Par lien : {l.pieces} pièce{l.pieces > 1 ? 's' : ''}
                {l.noms?.length ? ` (${l.noms.join(', ')})` : ''}
              </Typography>
              <Chip size="small" variant="outlined" color={etat.couleur} label={etat.texte} />
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}

/** Envoyer un message, et des pièces, au dossier relié. */
function Envoyer({ expertiseId, liaisons, pieces, onEnvoye }) {
  const [liaison, setLiaison] = useState(liaisons.length === 1 ? liaisons[0].id : '');
  const [objet, setObjet] = useState('');
  const [message, setMessage] = useState('');
  const [choisies, setChoisies] = useState([]);
  const [brouillon, setBrouillon] = useState(null);
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState('');

  const basculer = (id) => setChoisies((l) => (
    l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));

  const envoyer = async () => {
    setOccupe(true);
    setErreur('');
    try {
      const champs = {
        title: objet, title_bis: objet, comment: versHtml(message.trim()),
        hierarchy: 'PARTAGE', status: 'start',
      };
      // Si un premier essai a échoué, on renvoie la même tâche : pas de doublon.
      const tache = brouillon
        ? await majTache(brouillon, champs)
        : await creerTache(expertiseId, champs);
      setBrouillon(tache.id);
      const reponse = await envoyerAuDossierRelie(tache.id, liaison, choisies);
      setBrouillon(null);
      setObjet('');
      setMessage('');
      setChoisies([]);
      onEnvoye(reponse.detail);
    } catch (e) {
      setErreur(e?.response?.data?.detail || "L'envoi n'a pas abouti. Réessayez.");
    } finally {
      setOccupe(false);
    }
  };

  return (
    <Stack spacing={2}>
      {erreur && <Alert severity="error" onClose={() => setErreur('')}>{erreur}</Alert>}
      {liaisons.length > 1 && (
        <TextField select label="Destinataire" value={liaison}
                   onChange={(e) => setLiaison(e.target.value)}>
          {liaisons.map((l) => (
            <MenuItem key={l.id} value={l.id}>
              {l.organisation}{l.reference ? ` — dossier ${l.reference}` : ''}
            </MenuItem>
          ))}
        </TextField>
      )}
      <TextField label="Objet" value={objet} onChange={(e) => setObjet(e.target.value)} />
      <TextField label="Message" value={message} multiline minRows={3}
                 onChange={(e) => setMessage(e.target.value)} />
      {pieces.length > 0 && (
        <Box>
          <Typography variant="body2" color="text.secondary">
            Pièces à joindre
          </Typography>
          <Stack sx={{ maxHeight: 260, overflowY: 'auto' }}>
            {pieces.map((p) => (
              <FormControlLabel
                key={p.id}
                control={<Checkbox size="small" checked={choisies.includes(p.id)}
                                   onChange={() => basculer(p.id)} />}
                label={`${p.title || 'Sans titre'}${p.category ? ` — ${p.category}` : ''}`} />
            ))}
          </Stack>
        </Box>
      )}
      <Box>
        <Button variant="contained" onClick={envoyer}
                disabled={occupe || !liaison || !objet.trim()}>
          {occupe ? 'Envoi…' : 'Envoyer'}
        </Button>
      </Box>
    </Stack>
  );
}

/**
 * Le suivi du dossier : ce qu'il reste à faire, ce qu'on a reçu des dossiers
 * reliés, et ce qu'on leur envoie.
 */
export default function Suivi({ expertiseId, onRetour, onEtape }) {
  const [taches, setTaches] = useState(null);
  const [liaisons, setLiaisons] = useState([]);
  const [pieces, setPieces] = useState([]);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [occupe, setOccupe] = useState(false);
  const [rappel, setRappel] = useState({ title: '', review_at: '' });
  const [toutVoir, setToutVoir] = useState(false);

  const charger = useCallback(() => listerTaches(expertiseId)
    .then(setTaches)
    .catch(() => setErreur("Le suivi n'a pas pu être chargé.")), [expertiseId]);

  useEffect(() => {
    charger();
    listerLiaisons(expertiseId)
      .then((l) => setLiaisons(l.filter((x) => !x.rompue_le)))
      .catch(() => setLiaisons([]));
    listerToutesLesPieces(expertiseId)
      .then((l) => setPieces(l.filter((x) => x.fichier || x.file)))
      .catch(() => setPieces([]));
  }, [expertiseId, charger]);

  const agir = async (geste, reussite) => {
    setOccupe(true);
    setErreur('');
    try {
      const retour = await geste();
      setMessage(typeof reussite === 'function' ? reussite(retour) : reussite);
      await charger();
    } catch (e) {
      setErreur(e?.response?.data?.detail || "L'opération n'a pas abouti.");
    } finally {
      setOccupe(false);
    }
  };

  const traiterRecues = (ids, action) => agir(
    () => traiterPiecesRecues(ids, action),
    (r) => (r.refus?.length ? r.refus.join(' ')
      : `${r.faites} pièce${r.faites > 1 ? 's' : ''} ${action === 'reprendre'
        ? `reprise${r.faites > 1 ? 's' : ''} dans vos documents`
        : `écartée${r.faites > 1 ? 's' : ''}`}.`),
  );

  const ajouterRappel = () => agir(async () => {
    await creerTache(expertiseId, {
      title: rappel.title.trim(), status: 'start',
      review_at: rappel.review_at || null,
    });
    setRappel({ title: '', review_at: '' });
  }, 'Ajouté à votre liste.');

  const terminer = (tache) => agir(
    () => majTache(tache.id, { status: 'complete' }), 'Marqué comme fait.');

  if (taches === null) {
    return (
      <Layout titre="Suivi du dossier" onEtape={onEtape} etape="suivi">
        {erreur ? <Alert severity="error">{erreur}</Alert> : <CircularProgress size={28} />}
      </Layout>
    );
  }

  const visibles = taches.filter((t) => !estUneTrace(t));
  const ouverte = (t) => !['complete', 'delete'].includes(t.status);
  // Un envoi reçu reste en tête tant qu'une pièce attend une décision.
  const recus = visibles.filter(estRecu)
    .sort((a, b) => ouverte(b) - ouverte(a)
      || new Date(b.created_at) - new Date(a.created_at));
  const aFaire = visibles.filter((t) => ouverte(t) && !estFigee(t)
                                        && t.hierarchy !== 'PARTAGE');
  // Les courriels partis ont leur bloc : on y suit ce qui a été lu.
  const courriels = visibles.filter((t) => t.hierarchy === 'XMAIL')
    .sort((a, b) => new Date(b.modified_at) - new Date(a.modified_at));
  const faites = visibles
    .filter((t) => !estRecu(t) && t.status !== 'delete' && t.hierarchy !== 'XMAIL'
            && (!ouverte(t) || estFigee(t)))
    .sort((a, b) => new Date(b.modified_at) - new Date(a.modified_at));
  const historique = toutVoir ? faites : faites.slice(0, 8);

  return (
    <Layout
      etape="suivi" onEtape={onEtape}
      titre="Suivi du dossier"
      introduction="Ce qu'il reste à faire, ce que vous avez envoyé et à qui, et ce que vous échangez avec les dossiers reliés au vôtre."
      actions={<Button onClick={onRetour}>Retour au dossier</Button>}
    >
      <Stack spacing={3}>
        {erreur && <Alert severity="error" onClose={() => setErreur('')}>{erreur}</Alert>}
        {message && <Alert severity="success" onClose={() => setMessage('')}>{message}</Alert>}

        {recus.length > 0 && (
          <Bloc titre="Reçu des dossiers reliés"
                explication="Rien n'entre dans vos documents sans votre accord : reprenez les pièces utiles, écartez les autres.">
            {recus.map((t) => (
              <EnvoiRecu key={t.id} tache={t} occupe={occupe} onTraiter={traiterRecues} />
            ))}
          </Bloc>
        )}

        <Bloc titre="À faire">
          {aFaire.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Rien en attente.
            </Typography>
          )}
          {aFaire.map((t) => (
            <Stack key={t.id} direction="row" alignItems="center" spacing={2}
                   sx={{ py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body1">{t.title}</Typography>
                {t.review_at && (
                  <Typography variant="caption" color="text.secondary">
                    Pour le {dateCourte(t.review_at)}
                  </Typography>
                )}
              </Box>
              <Button size="small" variant="outlined" disabled={occupe}
                      onClick={() => terminer(t)}>
                C'est fait
              </Button>
            </Stack>
          ))}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
            <TextField size="small" label="Ajouter une chose à faire" sx={{ flexGrow: 1 }}
                       value={rappel.title}
                       onChange={(e) => setRappel((r) => ({ ...r, title: e.target.value }))} />
            <TextField size="small" type="date" label="Pour le"
                       InputLabelProps={{ shrink: true }} value={rappel.review_at}
                       onChange={(e) => setRappel((r) => ({ ...r, review_at: e.target.value }))} />
            <Button variant="contained" disabled={occupe || !rappel.title.trim()}
                    onClick={ajouterRappel}>
              Ajouter
            </Button>
          </Stack>
        </Bloc>

        {courriels.length > 0 && (
          <Bloc titre="Courriels envoyés"
                explication="Chaque envoi, avec ce qu'il portait. Les pièces envoyées par lien disent si elles ont été ouvertes.">
            {courriels.map((t) => <CourrielEnvoye key={t.id} tache={t} />)}
          </Bloc>
        )}

        <Bloc titre="Envoyer des documents par courriel"
              explication="Un devis, le rapport d'un autre expert, une facture, un plan : à un intervenant du dossier ou à une autre adresse.">
          <EnvoiCourriel expertiseId={expertiseId} libelleBouton="Envoyer"
                         onEnvoye={(r) => { setMessage(r.detail); charger(); }} />
        </Bloc>

        <Bloc titre="Envoyer à un dossier relié"
              explication={liaisons.length
                ? `Votre dossier est relié à ${liaisons.map((l) => l.organisation
                    + (l.reference ? ` (dossier ${l.reference})` : '')).join(', ')}. Ils ne voient que ce que vous leur envoyez.`
                : undefined}>
          {liaisons.length ? (
            <Envoyer expertiseId={expertiseId} liaisons={liaisons} pieces={pieces}
                     onEnvoye={(texte) => { setMessage(texte); charger(); }} />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Aucun dossier n'est relié au vôtre. Quand un expert ou un gestionnaire
              vous invite à relier vos dossiers, vous recevez un lien par courriel ;
              vous pourrez ensuite échanger des pièces ici.
            </Typography>
          )}
        </Bloc>

        {faites.length > 0 && (
          <Bloc titre="Historique">
            {historique.map((t) => (
              <Stack key={t.id} direction="row" spacing={2} alignItems="baseline"
                     sx={{ py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                  {dateCourte(t.modified_at)}
                </Typography>
                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                  {t.hierarchy === 'XPARTAGE' ? `Envoyé à ${t.receiver} : ` : ''}
                  {t.hierarchy === 'XPARTAGE' ? (t.title_bis || t.title) : t.title}
                </Typography>
              </Stack>
            ))}
            {faites.length > historique.length && (
              <Button size="small" onClick={() => setToutVoir(true)} sx={{ mt: 1 }}>
                Tout voir ({faites.length})
              </Button>
            )}
          </Bloc>
        )}
      </Stack>
    </Layout>
  );
}
